import { ChevronRight } from 'lucide-react';
import type { DashboardSection } from '../layout/Sidebar';

interface PipelineOverviewProps {
  counts: {
    new: number;
    queue: number;
    approved: number;
    uploaded: number;
    returned: number;
    issues: number;
    other: number;
  };
  activeSection: DashboardSection;
  onSectionClick: (section: DashboardSection) => void;
}

interface StageCardProps {
  label: string;
  sublabel: string;
  count: number;
  isActive: boolean;
  isWarning?: boolean;
  onClick: () => void;
}

interface StageCardPropsExtended extends StageCardProps {
  onboardingId?: string;
}

function StageCard({ label, sublabel, count, isActive, isWarning, onClick, onboardingId }: StageCardPropsExtended) {
  return (
    <button
      onClick={onClick}
      data-onboarding={onboardingId}
      className={`
        flex flex-col items-center justify-center px-4 py-3 rounded-lg border-2 transition-all min-w-[100px]
        hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-sm cursor-pointer
        ${isActive
          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 dark:border-purple-500'
          : isWarning && count > 0
            ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500'
            : 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-elevated'
        }
      `}
    >
      <span
        className={`text-2xl font-bold ${
          isActive
            ? 'text-purple-600 dark:text-purple-400'
            : isWarning && count > 0
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-800 dark:text-gray-100'
        }`}
      >
        {count}
      </span>
      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-[10px] text-gray-400 dark:text-gray-500">{sublabel}</span>
    </button>
  );
}

function Arrow() {
  return (
    <div className="flex items-center text-gray-300 dark:text-gray-600">
      <ChevronRight className="w-5 h-5" />
    </div>
  );
}

export function PipelineOverview({ counts, activeSection, onSectionClick }: PipelineOverviewProps) {
  return (
    <div className="bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Pipeline Overview
        </h3>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <StageCard
          label="New"
          sublabel="Download"
          count={counts.new}
          isActive={activeSection === 'new'}
          onClick={() => onSectionClick('new')}
          onboardingId="pipeline-new-card"
        />
        <Arrow />
        <StageCard
          label="Queue"
          sublabel="Active"
          count={counts.queue}
          isActive={activeSection === 'queue'}
          onClick={() => onSectionClick('queue')}
          onboardingId="pipeline-queue-card"
        />
        <Arrow />
        <StageCard
          label="Approved"
          sublabel="Ready"
          count={counts.approved}
          isActive={activeSection === 'approved'}
          onClick={() => onSectionClick('approved')}
          onboardingId="pipeline-approved-card"
        />
        <Arrow />
        <StageCard
          label="Uploaded"
          sublabel="Complete"
          count={counts.uploaded}
          isActive={activeSection === 'uploaded'}
          onClick={() => onSectionClick('uploaded')}
          onboardingId="pipeline-uploaded-card"
        />

        {/* Spacer to push right side items */}
        <div className="flex-1" />

        {/* Other link - small text link for ignored cases, aligned to bottom */}
        <button
          onClick={() => onSectionClick('other')}
          className={`
            text-xs px-2 py-1 rounded transition-colors self-end mb-1
            ${activeSection === 'other'
              ? 'text-purple-600 dark:text-purple-400 font-medium'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400'
            }
          `}
        >
          Other{counts.other > 0 && ` (${counts.other})`}
        </button>

        <StageCard
          label="Returned"
          sublabel="Re-check"
          count={counts.returned}
          isActive={activeSection === 'returned'}
          isWarning
          onClick={() => onSectionClick('returned')}
          onboardingId="pipeline-returned-card"
        />
        <div className="w-px h-12 bg-gray-200 dark:bg-dark-border mx-2" />
        <StageCard
          label="Issues"
          sublabel="Attention"
          count={counts.issues}
          isActive={activeSection === 'issues'}
          isWarning
          onClick={() => onSectionClick('issues')}
          onboardingId="pipeline-issues-card"
        />
      </div>
    </div>
  );
}
